/**
 * Service materials — the chef's pull list of disposables (utensils, plates,
 * trays & boats, containers) for a campus or a shipment. Rows come from PACKAGE
 * shipment lines; the kind comes from the server's per-SKU classification
 * (`KitchenShipmentLineSummary.packagingKind`). Pure — unit-tested.
 */

import type { KitchenShipmentLineSummary } from '@/api/kitchen-orders'

import type { Campus, ServiceMaterialKind, SupplyLine } from './types'

export const SERVICE_MATERIAL_KIND_ORDER: readonly ServiceMaterialKind[] = [
  'UTENSIL',
  'PLATE',
  'TRAY_BOAT',
  'BAG',
  'CONTAINER',
  'OTHER',
]

export const SERVICE_MATERIAL_KIND_LABELS: Record<ServiceMaterialKind, string> = {
  UTENSIL: 'Utensils',
  PLATE: 'Plates',
  TRAY_BOAT: 'Trays & boats',
  BAG: 'Bags',
  CONTAINER: 'Containers & packaging',
  OTHER: 'Other supplies',
}

export function serviceMaterialKindRank(kind: ServiceMaterialKind): number {
  const index = SERVICE_MATERIAL_KIND_ORDER.indexOf(kind)
  return index === -1 ? SERVICE_MATERIAL_KIND_ORDER.length : index
}

/**
 * Canonical pull-list order: by kind (utensils, plates, trays & boats,
 * containers, other), compostable stock first within a kind, then by name.
 */
export function compareServiceMaterials(a: SupplyLine, b: SupplyLine): number {
  return (
    serviceMaterialKindRank(a.kind) - serviceMaterialKindRank(b.kind) ||
    Number(b.isCompostable) - Number(a.isCompostable) ||
    a.name.localeCompare(b.name)
  )
}

export interface ServiceMaterialGroup {
  kind: ServiceMaterialKind
  label: string
  rows: SupplyLine[]
}

/** Rows bucketed by kind in canonical order; empty kinds are omitted. */
export function groupServiceMaterials(rows: ReadonlyArray<SupplyLine>): ServiceMaterialGroup[] {
  const byKind = new Map<ServiceMaterialKind, SupplyLine[]>()
  for (const row of [...rows].sort(compareServiceMaterials)) {
    const list = byKind.get(row.kind)
    if (list) {
      list.push(row)
    } else {
      byKind.set(row.kind, [row])
    }
  }
  return SERVICE_MATERIAL_KIND_ORDER.flatMap((kind) => {
    const list = byKind.get(kind)
    return list ? [{ kind, label: SERVICE_MATERIAL_KIND_LABELS[kind], rows: list }] : []
  })
}

/**
 * Tally PACKAGE lines with a packaging SKU into supply rows, summed per SKU.
 * Bulk primary lines stay out: their plannedQuantity is a SERVING count, not
 * a pan count (mirrors `shipmentsToCampuses`).
 */
export function serviceMaterialsFromLines(
  lines: ReadonlyArray<
    Pick<
      KitchenShipmentLineSummary,
      | 'lineRole'
      | 'packagingSkuId'
      | 'packagingSkuName'
      | 'packagingVariantTag'
      | 'packagingKind'
      | 'plannedQuantity'
      | 'isBulk'
    >
  >,
): SupplyLine[] {
  const bySku = new Map<string, SupplyLine>()
  for (const line of lines) {
    if (line.lineRole !== 'PACKAGE' || !line.packagingSkuId || line.plannedQuantity <= 0 || line.isBulk === true) {
      continue
    }
    const key = JSON.stringify([line.packagingSkuId, line.packagingVariantTag === 'COMPOSTABLE'])
    const existing = bySku.get(key)
    if (existing) {
      existing.qty += line.plannedQuantity
    } else {
      bySku.set(key, {
        skuId: line.packagingSkuId,
        name: line.packagingSkuName ?? 'Packaging',
        qty: line.plannedQuantity,
        isCompostable: line.packagingVariantTag === 'COMPOSTABLE',
        kind: line.packagingKind ?? 'OTHER',
      })
    }
  }
  return [...bySku.values()].sort(compareServiceMaterials)
}

/** One PACKAGE line → the supply row it contributes (`qty` = its planned count). */
export function supplyLineFrom(
  line: Pick<
    KitchenShipmentLineSummary,
    'packagingSkuId' | 'packagingSkuName' | 'packagingVariantTag' | 'packagingKind' | 'plannedQuantity'
  > & { packagingSkuId: string },
): SupplyLine {
  return {
    skuId: line.packagingSkuId,
    name: line.packagingSkuName ?? 'Packaging',
    qty: line.plannedQuantity,
    isCompostable: line.packagingVariantTag === 'COMPOSTABLE',
    kind: line.packagingKind ?? 'OTHER',
  }
}

/** Add one line's count into a per-SKU tally (creates the row on first sight). */
export function tallySupplyLine(
  into: Map<string, SupplyLine>,
  line: Pick<
    KitchenShipmentLineSummary,
    'packagingSkuId' | 'packagingSkuName' | 'packagingVariantTag' | 'packagingKind' | 'plannedQuantity'
  >,
): void {
  if (!line.packagingSkuId) {
    return
  }
  const key = JSON.stringify([line.packagingSkuId, line.packagingVariantTag === 'COMPOSTABLE'])
  const existing = into.get(key)
  if (existing) {
    existing.qty += line.plannedQuantity
  } else {
    into.set(key, supplyLineFrom({ ...line, packagingSkuId: line.packagingSkuId }))
  }
}

/** Lines with a SKU and a positive count → supply rows summed per SKU, in pull order. */
export function supplyLinesFrom(
  lines: ReadonlyArray<
    Pick<
      KitchenShipmentLineSummary,
      'packagingSkuId' | 'packagingSkuName' | 'packagingVariantTag' | 'packagingKind' | 'plannedQuantity'
    >
  >,
): SupplyLine[] {
  const bySku = new Map<string, SupplyLine>()
  for (const line of lines) {
    if (line.packagingSkuId && line.plannedQuantity > 0) {
      tallySupplyLine(bySku, line)
    }
  }
  return [...bySku.values()].sort(compareServiceMaterials)
}

/** Sum two supply lists per SKU (never mutates the inputs), in pull order. */
export function mergeSupplyLines(base: ReadonlyArray<SupplyLine>, extra: ReadonlyArray<SupplyLine>): SupplyLine[] {
  const bySku = new Map<string, SupplyLine>()
  for (const row of [...base, ...extra]) {
    const key = JSON.stringify([row.skuId, row.isCompostable])
    const existing = bySku.get(key)
    if (existing) {
      existing.qty += row.qty
    } else {
      bySku.set(key, { ...row })
    }
  }
  return [...bySku.values()].sort(compareServiceMaterials)
}

/** "120× Fork · 120× Paper Boat" for a deliverable's supplies column; null when empty. */
export function formatSupplyLines(rows: ReadonlyArray<SupplyLine> | undefined): string | null {
  if (!rows || rows.length === 0) {
    return null
  }
  return rows.map((row) => `${row.qty.toLocaleString()}× ${row.name}`).join(' · ')
}

/** Where a campus-level supply row came from — the INGREDIENTS Supplies table shows both. */
export type CampusSupplySource = 'SUPPLY' | 'PACKAGING'

export interface CampusSupplyRow extends SupplyLine {
  source: CampusSupplySource
}

/**
 * The INGREDIENTS-tab Supplies table: every campus's SUPPLIES (disposables
 * shipped with the meals) and PACKAGING (what the food is served in), summed
 * per SKU across campuses, supplies first. The two never merge with each
 * other even for the same SKU — they are different pulls.
 */
export function campusSupplyRows(campuses: ReadonlyArray<Campus>): CampusSupplyRow[] {
  const supplies = mergeSupplyLines(
    [],
    campuses.flatMap((campus) => campus.supplies),
  )
  const packaging = mergeSupplyLines(
    [],
    campuses.flatMap((campus) => campus.packaging ?? []),
  )
  return [
    ...supplies.map((row) => ({ ...row, source: 'SUPPLY' as const })),
    ...packaging.map((row) => ({ ...row, source: 'PACKAGING' as const })),
  ]
}

/** Count bags as pulls too, without changing their attachments or the food's container labels. */
export function visibleSupplyLines(
  rows: ReadonlyArray<SupplyLine | CampusSupplyRow>,
  includePackaging = false,
): SupplyLine[] {
  return mergeSupplyLines(
    [],
    rows.filter(
      (row) =>
        includePackaging ||
        !('source' in row) ||
        row.source === 'SUPPLY' ||
        row.kind === 'UTENSIL' ||
        row.kind === 'BAG',
    ),
  ).sort((a, b) => a.name.localeCompare(b.name))
}
